import { reactive } from "vue";

// Signup v2 flow graph, options, and shared state.
// Ported from ditto-proto src/components/onboarding/OnboardingWizardV2.tsx @ 1d75490.
// Copy and option lists mirror the prototype constants — treat the prototype
// as the source of truth for UX and copy at time of handoff (FLOCK-6611).

// --- Wizard steps (progress bar, slug routed) ---

export interface IWizardStep {
  slug: string;
  label: string;
}

export const WIZARD_STEPS: IWizardStep[] = [
  { slug: "about-you", label: "About You" },
  { slug: "organization", label: "Organization" },
  { slug: "account", label: "Account" },
  { slug: "verify-email", label: "Verify Email" }
];

export const WIZARD_TOTAL = WIZARD_STEPS.length;

// Side-flow screens — no progress bar.
export const USE_DITTO = "use-ditto";
export const TELL_ADMIN = "tell-admin";
export const CONNECT_ROOM = "connect-room";
export const SHARE_METHOD = "share-method";

export const SIDE_FLOW_SLUGS = [
  USE_DITTO,
  TELL_ADMIN,
  CONNECT_ROOM,
  SHARE_METHOD
];

// Job functions that branch into the use-ditto end-user path instead of
// continuing through the signup wizard.
export const END_USER_JOB_FUNCTIONS = ["Student", "Teacher / Professor"];

// Instant client-side check for the most common free-mail domain. The full
// free and disposable provider list lives on the API (freeEmailDomains.ts) and
// reaches the wizard through GET /api/users/emailavailable (reason
// 'free_email'); add domains there, not here.
export const FREE_EMAIL_DOMAINS = ["gmail.com"];

export function isFreeEmailDomain(email: string): boolean {
  const domain = email.split("@")[1];
  if (!domain) return false;
  return FREE_EMAIL_DOMAINS.includes(domain.toLowerCase().trim());
}

// --- Password policy ---
//
// The single source of truth for password rules in the wizard. Signup v2
// creates the Auth0 user itself (POST /dbconnections/signup), so anything this
// lets through comes back as a server-side rejection on the Account step —
// these rules MUST mirror the password policy configured on the Auth0 database
// connection. The values below match Auth0's "Good" policy as a safe default:
// 8+ characters with at least one lower case letter, one upper case letter and
// one number. If the tenant policy changes, change this with it.
export const PASSWORD_MIN_LENGTH = 8;

export function passwordPolicyError(password: string): string {
  if (!password) return "This field is required.";
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (!/[a-z]/.test(password)) {
    return "Password must include a lower case letter.";
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must include an upper case letter.";
  }
  if (!/\d/.test(password)) {
    return "Password must include a number.";
  }
  return "";
}

// The internal connect-room flow is parked; the use-ditto CTA links here.
// Swap the CTA back to `navigate(CONNECT_ROOM)` in StepUseDitto to re-enable.
export const EXTERNAL_CONNECT_URL = "https://connect.goditto.com/";

// Brand purple used on the connect-room and share-method screens.
export const BRAND_PURPLE = "#7D2EFF";

// The single room code the parked connect flow recognizes. Triggers the
// Ditto → customer brand transition (per-organization receiver branding demo).
export const VALID_ROOM_CODE = "ROOM123";

export function normalizeRoomCode(code: string): string {
  return code.replace(/\s+/g, "").toUpperCase();
}

export function wizardIndexFromSlug(slug: string | undefined): number {
  if (!slug) return -1;
  return WIZARD_STEPS.findIndex(s => s.slug === slug);
}

// --- Dropdown options ---

export const MARKETS = [
  "Business",
  "Government",
  "Higher Ed",
  "K12",
  "Religious / Church",
  "Home",
  "Other"
];

// Student and Teacher sit at the bottom, just above Other: they route to the
// no-account path rather than continuing the wizard, so they should not lead
// the list (Nisreen, post-demo feedback).
export const JOB_FUNCTIONS = [
  "IT / Technology",
  "Network / Security",
  "School / Site Administrator",
  "District Administrator",
  "Communications / Marketing",
  "Executive",
  "Facility Management",
  "Owner",
  "Student",
  "Teacher / Professor",
  "Other"
];

export const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "India",
  "Japan",
  "Mexico",
  "Brazil",
  "Other"
];

// Must match the in-form options of HubSpot's ditto_screen_count property
// exactly, or the form submission is rejected. The prototype's ranges
// (1-10 / 11-50 / 51-100 / 101-500 / 500+) are not usable: 1-10 and 11-50
// exist on the property but are excluded from forms, the rest never existed.
export const DISPLAY_COUNTS = [
  "1-19",
  "20-50",
  "51-150",
  "151-500",
  "501-1000",
  "1000 +"
];

export const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming"
];

// --- Wizard-level form state (survives back-navigation between steps) ---

export interface ISignupFormState {
  // about-you
  firstName: string;
  lastName: string;
  email: string;
  jobFunction: string;
  phone: string;
  country: string;
  state: string;
  // organization
  orgName: string;
  market: string;
  numDisplays: string;
  plannedUse: string;
  // account
  password: string;
  confirmPassword: string;
  agreedToTerms: boolean;
  // side flow
  hasDitto: boolean;
  roomCode: string;
  // attribution
  pid: string | null;
  // backend results
  registeredUserId: string | null;
  registeredOrgId: string | null;
  auth0Signature: string | null;
}

export function createSignupForm(): ISignupFormState {
  return reactive({
    firstName: "",
    lastName: "",
    email: "",
    jobFunction: "",
    phone: "",
    country: "",
    state: "",
    orgName: "",
    market: "",
    numDisplays: "",
    plannedUse: "",
    password: "",
    confirmPassword: "",
    agreedToTerms: false,
    hasDitto: false,
    roomCode: "",
    pid: null,
    registeredUserId: null,
    registeredOrgId: null,
    auth0Signature: null
  });
}

// --- Mobile banner specs (compact strip below lg) ---

export interface IBannerSpec {
  icon?: string;
  logo?: boolean;
  text: string;
}

export const MOBILE_BANNERS: Record<string, IBannerSpec> = {
  "about-you": {
    logo: true,
    text: "Screen sharing, digital signage, and emergency alerts"
  },
  organization: {
    icon: "buildings",
    text: "30-day free trial with unlimited receivers"
  },
  account: { icon: "lock", text: "One step away from the Ditto portal" },
  "verify-email": {
    icon: "envelope-open",
    text: "Check your email for a verification code"
  },
  "use-ditto": {
    icon: "broadcast",
    text: "Connect to a Ditto display in seconds — no account needed"
  },
  "tell-admin": {
    icon: "paper-plane-tilt",
    text: "Help bring Ditto to your school or business"
  }
};
